import "./LeadLists.scss"

import { debounce } from "lodash"
import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { getAllLists } from "../../../redux/list/listActions"
import { getMaxOnPage, setMaxOnPageToLocalStorage } from "../../../utils/app-utils"
import { useGlobalTranslation } from "../../../utils/hook"
import RegularLayout from "../../layouts/RegularLayout"
import LeadListsTable from "../../organisms/LeadListsTable"

export const LeadListsPage = () => {
  const dispatch = useDispatch()
  const { tt } = useGlobalTranslation()

  const { total } = useSelector(state => ({
    total: state.list.listsPagination.total,
  }))

  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState("ASC")
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [searchValue, setSearchValue] = useState(null)
  const [selectedMaxOnPage, setSelectedMaxOnPage] = useState(getMaxOnPage())

  const fetchLists = useMemo(() => {
    return async ({
      search = searchValue,
      skip = page * selectedMaxOnPage,
      take = selectedMaxOnPage,
      direction = sortDirection,
      column = sortColumn,
    } = {}) => {
      setLoading(true)
      await dispatch(
        getAllLists({
          search,
          skip,
          take,
          direction,
          column,
        }),
      )
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  useEffect(() => {
    const initializeLists = async () => {
      await fetchLists({ search: "" })
    }

    initializeLists()
  }, [fetchLists])

  const debouncedFetchLists = useMemo(() => {
    return debounce(async search => {
      setPage(0)
      await fetchLists({ search, skip: 0 })
    }, 500)
  }, [fetchLists])

  useEffect(() => {
    if (searchValue !== null) {
      debouncedFetchLists(searchValue)
    }

    return () => {
      debouncedFetchLists.cancel()
    }
  }, [searchValue, debouncedFetchLists])

  const handleChangeMaxOnPage = listsPerPage => {
    setSelectedMaxOnPage(listsPerPage)
    setMaxOnPageToLocalStorage(listsPerPage)
    setPage(0)
    fetchLists({ skip: 0, take: listsPerPage })
  }

  const handleChangePage = newPage => {
    setPage(newPage)
    fetchLists({ skip: newPage * selectedMaxOnPage })
  }

  const handleSort = (column, direction) => {
    setSortColumn(column)
    setSortDirection(direction)
    setPage(0)
    fetchLists({ skip: 0, direction: sortDirection })
  }

  return (
    <RegularLayout title={tt("lead-lists")}>
      <LeadListsTable
        fetchLists={fetchLists}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        totalCount={total}
        page={page}
        onPageChange={handleChangePage}
        maxOnPage={selectedMaxOnPage}
        handleChangeMaxOnPage={handleChangeMaxOnPage}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
      />
    </RegularLayout>
  )
}
